import { useState, useRef, useEffect, useCallback } from 'react'
import { useTowers, useToggleTowers } from '../tower'
import { useUser } from '../user'

const DEBOUNCE_DELAY = 3000 // 3 seconds

/**
 * Helper to check if org_ids array is empty or null
 */
const is_org_ids_empty = (org_ids: any) => !org_ids || !Array.isArray(org_ids) || org_ids.length === 0

/**
 * Determines if a tower is toggled for extra features using the new twenty_four_hrs_forecast_toggled field
 */
const is_tower_toggled = (tower: any) => tower?.twenty_four_hrs_forecast_toggled?.value === true

/**
 * Determines if a tower was toggled by the user themselves (not via team)
 * A tower is "self-toggled" when:
 * - twenty_four_hrs_forecast_toggled.self === true AND
 * - twenty_four_hrs_forecast_toggled.org_ids is empty/null
 */
const is_self_toggled = (tower: any) => {
	const toggle_data = tower?.twenty_four_hrs_forecast_toggled
	return toggle_data?.self === true && is_org_ids_empty(toggle_data?.org_ids)
}

/**
 * Determines if a tower's access comes from team membership
 * A tower is "team-toggled" when:
 * - twenty_four_hrs_forecast_toggled.value === true AND
 * - twenty_four_hrs_forecast_toggled.org_ids is NOT empty
 */
const is_team_toggled = (tower: any) => {
	const toggle_data = tower?.twenty_four_hrs_forecast_toggled
	return toggle_data?.value === true && !is_org_ids_empty(toggle_data?.org_ids)
}

/**
 * Shared hook for managing tower 24H forecast toggle functionality
 * Used by Search.tsx, DataCard.tsx, ToggleTowerPopup.tsx, and FormSelectTowers.tsx
 *
 * Key logic:
 * - Quota counting: Only counts towers where self===true AND org_ids is empty
 * - Toggle allowed: User can toggle ON if under quota
 * - Untoggle allowed: Only if self===true AND org_ids is empty (team-toggled towers cannot be untoggled)
 * - Debounced mutations: Changes are batched and sent after 3 seconds of inactivity
 */
export const useToggleTowerForecast = (options?: { onCompleted?: () => void, on_error?: (err: any) => void }) => {
	const user_op = useUser()
	const tower_op = useTowers({ user: true, latestData: true })

	// Pending changes state: tracks tower IDs that should be toggled ON (local optimistic state)
	const [pending_toggle_ids, set_pending_toggle_ids] = useState<Set<string> | null>(null)
	const [has_pending_changes, set_has_pending_changes] = useState(false)

	// Refs for debounce timer and tracking if mutation is in flight
	const debounce_timer_ref = useRef<NodeJS.Timeout | null>(null)
	const is_mutating_ref = useRef(false)
	const pending_ids_ref = useRef<Set<string> | null>(null)

	// Track the last successfully sent toggle IDs to compare with server state
	const last_sent_ids_ref = useRef<Set<string> | null>(null)

	const [toggle_towers, toggle_towers_op] = useToggleTowers({
		onCompleted: () => {
			is_mutating_ref.current = false
			set_has_pending_changes(false)
			// Don't reset pending_toggle_ids here - we keep it until server state matches
			// The useEffect below will clear it when server state catches up
			if (options?.onCompleted)
				options.onCompleted()
		},
		on_error: (err: any) => {
			is_mutating_ref.current = false
			// Keep pending changes so user can retry
			if (options?.on_error)
				options.on_error(err)
		}
	})

	const plan = user_op?.data?.plan?.name
	const is_free = plan == 'free'
	const is_basic = plan == 'solo'  // Note: plan key is 'basic' but name is 'solo'
	const is_pro = plan == 'professional'
	const is_org = plan == 'organisation' || plan == 'organization'

	// Calculate tower 24H limit based on plan
	const tower_24h_limit = is_free ? 0 : (is_basic || is_pro) ? 10 : 10000000000

	// Get plan name for display
	const get_plan_name = useCallback(() => {
		if (is_basic) return 'Solo'
		if (is_pro) return 'Professional'
		if (is_org) return 'Organization'
		return 'Free'
	}, [is_basic, is_pro, is_org])

	/**
	 * Get the server-confirmed self-toggled tower IDs
	 */
	const get_server_self_toggled_ids = useCallback(() => {
		const towers = tower_op?.data?.towers || []
		return towers.filter((t: any) => is_self_toggled(t)).map((t: any) => t.id)
	}, [tower_op?.data?.towers])

	// Clear pending state when server state matches our last sent state
	useEffect(() => {
		if (last_sent_ids_ref.current === null || pending_toggle_ids === null) return

		const server_ids = new Set(get_server_self_toggled_ids())
		const sent_ids = last_sent_ids_ref.current

		// Check if server state matches what we sent
		const server_matches_sent =
			server_ids.size === sent_ids.size &&
			Array.from(sent_ids).every(id => server_ids.has(id))

		if (server_matches_sent) {
			// Server has caught up, clear pending state
			pending_ids_ref.current = null
			set_pending_toggle_ids(null)
			last_sent_ids_ref.current = null
		}
	}, [tower_op?.data?.towers, pending_toggle_ids, get_server_self_toggled_ids])

	/**
	 * Get the effective self-toggled IDs (considering pending changes)
	 */
	const get_effective_self_toggled_ids = useCallback(() => {
		if (pending_toggle_ids !== null) {
			return Array.from(pending_toggle_ids)
		}
		return get_server_self_toggled_ids()
	}, [pending_toggle_ids, get_server_self_toggled_ids])

	/**
	 * Count towers that the user has personally toggled (counts against quota)
	 * Considers pending changes for accurate quota tracking
	 */
	const get_self_toggled_count = useCallback(() => {
		return get_effective_self_toggled_ids().length
	}, [get_effective_self_toggled_ids])

	// Keep toggle_towers in a ref to avoid stale closures in setTimeout
	const toggle_towers_ref = useRef(toggle_towers)
	toggle_towers_ref.current = toggle_towers

	/**
	 * Execute the mutation immediately (used for flush and debounce callback)
	 * Uses refs to avoid stale closure issues with setTimeout
	 */
	const execute_mutation = useCallback(() => {
		const ids_to_send = pending_ids_ref.current
		if (ids_to_send === null) return

		is_mutating_ref.current = true
		// Save the IDs we're sending so we can compare with server state later
		last_sent_ids_ref.current = new Set(ids_to_send)
		// Use ref to get the latest toggle_towers function
		toggle_towers_ref.current(Array.from(ids_to_send))
	}, [])

	/**
	 * Flush pending changes immediately (e.g., when navigating away)
	 */
	const flush_pending_changes = useCallback(() => {
		if (debounce_timer_ref.current) {
			clearTimeout(debounce_timer_ref.current)
			debounce_timer_ref.current = null
		}

		if (pending_ids_ref.current !== null && !is_mutating_ref.current) {
			execute_mutation()
		}
	}, [execute_mutation])

	/**
	 * Schedule debounced mutation
	 */
	const schedule_mutation = useCallback(() => {
		// Clear existing timer
		if (debounce_timer_ref.current) {
			clearTimeout(debounce_timer_ref.current)
		}

		// Set new timer
		debounce_timer_ref.current = setTimeout(() => {
			debounce_timer_ref.current = null
			if (!is_mutating_ref.current) {
				execute_mutation()
			}
		}, DEBOUNCE_DELAY)
	}, [execute_mutation])

	// Cleanup on unmount - flush pending changes
	useEffect(() => {
		return () => {
			if (debounce_timer_ref.current) {
				clearTimeout(debounce_timer_ref.current)
			}
			// Flush on unmount
			if (pending_ids_ref.current !== null && !is_mutating_ref.current) {
				// We need to call the mutation directly here
				// But since we're unmounting, we'll rely on the beforeunload handler
			}
		}
	}, [])

	// Handle page unload - flush pending changes
	useEffect(() => {
		const handle_before_unload = (e: BeforeUnloadEvent) => {
			if (pending_ids_ref.current !== null && !is_mutating_ref.current) {
				flush_pending_changes()
				// Show browser warning
				e.preventDefault()
				e.returnValue = ''
			}
		}

		window.addEventListener('beforeunload', handle_before_unload)
		return () => {
			window.removeEventListener('beforeunload', handle_before_unload)
		}
	}, [flush_pending_changes])

	/**
	 * Check if user can add another tower to 24H forecast
	 * @param tower_id Optional tower ID to check. If not provided, just checks current usage
	 * @returns Object with can (boolean), current usage, max limit, and optional reason
	 */
	const can_add_tower = useCallback((tower_id?: string) => {
		const current_self_toggled = get_self_toggled_count()

		// For free users, always return false (hide quota display for free users)
		if (is_free) {
			return {
				can: false,
				current: 0,
				max: 0,
				reason: 'Free plan does not support 24H forecast. Please upgrade to Solo, Professional, or Organisation plan.'
			}
		}

		// For organization, always return true (unlimited)
		if (is_org) {
			return {
				can: true,
				current: current_self_toggled,
				max: tower_24h_limit,
				plan_name: get_plan_name()
			}
		}

		// For basic/pro, check if under limit
		// If tower_id provided, simulate adding it
		const effective_ids = get_effective_self_toggled_ids()
		const already_in_list = tower_id ? effective_ids.includes(tower_id) : false
		const projected_count = tower_id && !already_in_list ? current_self_toggled + 1 : current_self_toggled

		if (projected_count > tower_24h_limit) {
			return {
				can: false,
				current: current_self_toggled,
				max: tower_24h_limit,
				plan_name: get_plan_name(),
				reason: `Your ${get_plan_name()} plan allows up to ${tower_24h_limit} towers with 24H forecast. You are currently using ${current_self_toggled}/${tower_24h_limit}.`
			}
		}

		return {
			can: true,
			current: current_self_toggled,
			max: tower_24h_limit,
			plan_name: get_plan_name()
		}
	}, [get_self_toggled_count, get_effective_self_toggled_ids, is_free, is_org, tower_24h_limit, get_plan_name])

	/**
	 * Check if a tower can be untoggled by the user
	 * @param tower_id Tower ID to check
	 * @returns Object with can_untoggle (boolean) and reason if not allowed
	 */
	const can_untoggle_tower = useCallback((tower_id: string) => {
		const towers = tower_op?.data?.towers || []
		const tower = towers.find((t: any) => t.id === tower_id)

		if (!tower) {
			return { can_untoggle: false, reason: 'Tower not found.' }
		}

		// If tower is team-toggled (org_ids not empty), user cannot untoggle
		if (is_team_toggled(tower)) {
			return {
				can_untoggle: false,
				is_team_toggled: true,
				reason: 'This tower was enabled by one of your teams. You cannot disable it, but it does not count against your personal quota.'
			}
		}

		// Check if tower is currently toggled (server state or pending state)
		const effective_ids = get_effective_self_toggled_ids()
		const is_currently_toggled = effective_ids.includes(tower_id) || is_tower_toggled(tower)

		if (!is_currently_toggled) {
			return { can_untoggle: false, reason: 'Tower is not toggled.' }
		}

		return { can_untoggle: true }
	}, [tower_op?.data?.towers, get_effective_self_toggled_ids])

	/**
	 * Toggle tower 24H forecast on/off with debouncing
	 * @param tower_id Tower ID to toggle
	 * @param enable True to enable, false to disable
	 * @param set_switch Optional callback to update UI switch state
	 * @returns Object with success status, and error/team_toggled_message if applicable
	 */
	const toggle_tower = useCallback((tower_id: string, enable: boolean, set_switch?: (val: boolean) => void) => {
		// If trying to disable (untoggle)
		if (!enable) {
			const untoggle_check = can_untoggle_tower(tower_id)
			if (!untoggle_check.can_untoggle) {
				if (set_switch) {
					// Reset switch to ON state since we can't untoggle
					set_switch(true)
				}
				return {
					success: false,
					is_team_toggled: untoggle_check.is_team_toggled || false,
					error: untoggle_check.reason
				}
			}
		}

		// Get current effective state from ref (synchronously updated)
		// This ensures we always have the latest state even with rapid toggles
		const current_ids = pending_ids_ref.current !== null
			? new Set(pending_ids_ref.current)
			: new Set(get_server_self_toggled_ids())

		// Apply the change
		if (enable) {
			// Check quota before adding
			if (!current_ids.has(tower_id)) {
				const new_count = current_ids.size + 1
				if (new_count > tower_24h_limit) {
					if (set_switch)
						set_switch(false)
					return {
						success: false,
						error: 'Max. 24H towers exceeded. Please upgrade or unselect towers.'
					}
				}
				current_ids.add(tower_id)
			}
		} else {
			current_ids.delete(tower_id)
		}

		// Update UI immediately (optimistic)
		if (set_switch)
			set_switch(enable)

		// Update pending state (both ref and state)
		// Ref is updated synchronously so debounce timer has the latest value
		pending_ids_ref.current = current_ids
		set_pending_toggle_ids(current_ids)
		set_has_pending_changes(true)

		// Schedule debounced mutation
		schedule_mutation()

		return {
			success: true
		}
	}, [get_server_self_toggled_ids, can_untoggle_tower, tower_24h_limit, schedule_mutation])

	/**
	 * Get tower toggle info for a specific tower
	 * Useful for UI to determine how to display toggle state
	 * Considers pending changes for accurate display
	 */
	const get_tower_toggle_info = useCallback((tower_id: string) => {
		const towers = tower_op?.data?.towers || []
		const tower = towers.find((t: any) => t.id === tower_id)

		if (!tower) {
			return {
				is_toggled: false,
				is_self_toggled: false,
				is_team_toggled: false,
				can_toggle_on: !is_free,
				can_toggle_off: false,
				is_pending: false
			}
		}

		const team_toggle = is_team_toggled(tower)
		const server_self_toggle = is_self_toggled(tower)

		// Determine effective toggle state (considering pending changes)
		const effective_ids = get_effective_self_toggled_ids()
		const is_in_pending = effective_ids.includes(tower_id)

		// A tower is effectively toggled if:
		// - It's team-toggled (always shows as toggled), OR
		// - It's in the pending/effective self-toggled list
		const effectively_toggled = team_toggle || is_in_pending

		// Check if this tower's state differs from server state (pending)
		const is_pending = pending_toggle_ids !== null && (
			(is_in_pending && !server_self_toggle) ||
			(!is_in_pending && server_self_toggle)
		)

		return {
			is_toggled: effectively_toggled,
			is_self_toggled: is_in_pending,
			is_team_toggled: team_toggle,
			can_toggle_on: !is_free && !effectively_toggled,
			can_toggle_off: effectively_toggled && !team_toggle,
			is_pending: is_pending
		}
	}, [tower_op?.data?.towers, is_free, get_effective_self_toggled_ids, pending_toggle_ids])

	/**
	 * Retry failed mutation with current pending state
	 */
	const retry_pending_changes = useCallback(() => {
		if (pending_toggle_ids !== null && !is_mutating_ref.current) {
			execute_mutation()
		}
	}, [pending_toggle_ids, execute_mutation])

	return {
		can_add_tower,
		can_untoggle_tower,
		toggle_tower,
		get_tower_toggle_info,
		get_self_toggled_count,
		flush_pending_changes,
		retry_pending_changes,
		has_pending_changes,
		loading: toggle_towers_op.loading || user_op.loading || tower_op.loading,
		is_saving: toggle_towers_op.loading,
		towers: tower_op?.data?.towers || [],
		plan,
		is_free,
		is_basic,
		is_pro,
		is_org
	}
}
